import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Footer from '../components/Footer';
import { BarChart } from 'react-native-chart-kit';
import { formatCurrency } from '../utils/formatters';

const FlatVsReducingRateCalculatorScreen = () => {
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    principal: '500000',
    years: '5',
    flatRate: '10',
    reducingRate: '10',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateComparison = () => {
    const P = parseFloat(formData.principal);
    const years = parseFloat(formData.years);
    const flatRate = parseFloat(formData.flatRate);
    const reducingRate = parseFloat(formData.reducingRate);

    if ([P, years, flatRate, reducingRate].some(v => isNaN(v) || v <= 0)) { setResults(null); return; }

    const months = years * 12;
    // Flat interest calculation
    const totalInterestFlat = P * (flatRate / 100) * years;
    const emiFlat = (P + totalInterestFlat) / months;

    // Reducing balance EMI
    const rMonthly = reducingRate / 100 / 12;
    const emiReducing = (P * rMonthly * Math.pow(1 + rMonthly, months)) / (Math.pow(1 + rMonthly, months) - 1);
    const totalInterestReducing = emiReducing * months - P;

    const savings = Math.max(0, Math.round(totalInterestFlat - totalInterestReducing));

    setResults({
      principal: Math.round(P),
      emiFlat: Math.round(emiFlat),
      emiReducing: Math.round(emiReducing),
      totalInterestFlat: Math.round(totalInterestFlat),
      totalInterestReducing: Math.round(totalInterestReducing),
      savings,
      years,
    });
  };

  useEffect(() => { calculateComparison(); }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>Flat vs Reducing Rate Calculator</Text>
        <Text style={styles.heroSubtitle}>Compare EMI and total interest across flat and reducing methods</Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={styles.calculatorRow}>
        <View style={styles.formContent}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Loan Amount</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput style={styles.input} value={formData.principal} onChangeText={(v)=>handleInputChange('principal',v)} keyboardType="numeric" placeholder="500000" />
            </View>
          </View>

          <View style={styles.inputRow}>
            <View style={[styles.inputGroup, styles.half]}>
              <Text style={styles.label}>Tenure</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.years} onChangeText={(v)=>handleInputChange('years',v)} keyboardType="numeric" placeholder="5" />
                <Text style={styles.suffixText}>Years</Text>
              </View>
            </View>
            <View style={[styles.inputGroup, styles.half]}>
              <Text style={styles.label}>Flat Rate (p.a.)</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.flatRate} onChangeText={(v)=>handleInputChange('flatRate',v)} keyboardType="numeric" placeholder="10" />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Reducing Rate (p.a.)</Text>
            <View style={styles.inputContainer}>
              <TextInput style={styles.input} value={formData.reducingRate} onChangeText={(v)=>handleInputChange('reducingRate',v)} keyboardType="numeric" placeholder="10" />
              <Text style={styles.percentSymbol}>%</Text>
            </View>
            <Text style={styles.helperText}>Reducing balance EMI uses monthly compounding on remaining principal.</Text>
          </View>
        </View>

        <View style={styles.summaryPanel}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Comparison Summary</Text>
            <Text style={styles.panelSubtitle}>EMI and Total Interest</Text>
          </View>
          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>EMI (Flat)</Text><Text style={styles.summaryValue}>{formatCurrency(results.emiFlat)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>EMI (Reducing)</Text><Text style={styles.summaryValue}>{formatCurrency(results.emiReducing)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Interest (Flat)</Text><Text style={styles.summaryValue}>{formatCurrency(results.totalInterestFlat)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Interest (Reducing)</Text><Text style={styles.summaryValue}>{formatCurrency(results.totalInterestReducing)}</Text></View>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Interest Saved (vs Flat)</Text><Text style={styles.totalValue}>{formatCurrency(results.savings)}</Text></View>
              {renderChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderChart = () => {
    if (!results) return null;
    const chartWidth = isMobile ? Math.min(screenData.width - 40, 340) : 340;
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Total Interest Comparison</Text>
        <BarChart
          data={{ labels: ['Flat', 'Reducing'], datasets: [{ data: [results.totalInterestFlat, results.totalInterestReducing] }] }}
          width={chartWidth}
          height={180}
          yAxisLabel="₹"
          chartConfig={{
            backgroundColor: '#ffffff',
            backgroundGradientFrom: '#ffffff',
            backgroundGradientTo: '#ffffff',
            decimalPlaces: 0,
            color: () => '#10B981',
            labelColor: () => '#0f172a',
          }}
          style={{ marginTop: 8 }}
        />
      </View>
    );
  };

  const renderInfo = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>Understanding the Methods</Text>
      <Text style={styles.sectionText}>Flat interest calculates interest on the original principal for the entire tenure, leading to higher total interest. Reducing balance EMI recalculates interest each month on the remaining principal, typically resulting in lower interest paid.</Text>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={20} color="#1f2937" />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Which method do banks use?</Text>
            <Text style={styles.faqAnswer}>Banks use reducing balance for EMIs. Some finance offers quote flat rates—always compare reducing EMI for accuracy.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Why does flat interest look cheaper?</Text>
            <Text style={styles.faqAnswer}>Flat interest quotes a lower rate but charges interest on full principal throughout, making effective annual rate higher.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How do I choose?</Text>
            <Text style={styles.faqAnswer}>Prefer reducing balance EMI for transparency and fair comparison across lenders.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Does prepayment affect reducing EMI?</Text>
            <Text style={styles.faqAnswer}>Yes. Prepayments reduce remaining principal, lowering interest in subsequent months.</Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSEO = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Flat vs Reducing Rate: EMI and Interest Comparison</Text>
      <Text style={styles.sectionText}>Use this calculator to compare EMIs and total interest between flat interest and reducing balance methods. Reducing balance generally leads to lower interest outgo for the same rate and tenure.</Text>

      <Text style={styles.seoSubtitle}>Related calculators</Text>
      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('EMICalculator')}>
          <Text style={styles.linkText}>EMI Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('HomeLoanEMICalculator')}>
          <Text style={styles.linkText}>Home Loan EMI</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('CarLoanEMICalculator')}>
          <Text style={styles.linkText}>Car Loan EMI</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.disclaimer}>Illustrative comparison. Actual EMIs may vary with fees and rounding.</Text>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      {renderCalculator()}
      {renderInfo()}
      {renderFAQ()}
      {renderSEO()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 24, paddingHorizontal: 30 },
  heroContent: { width: '100%', alignSelf: 'stretch' },
  heroTitle: { fontSize: 28, fontWeight: 'bold', color: '#ffffff', marginBottom: 6 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 30, paddingVertical: 20 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  formContent: { gap: 20, flex: 0.7 },
  summaryPanel: { flex: 0.3, backgroundColor: '#ffffff', borderRadius: 12, padding: 20, height: 'fit-content', borderWidth: 1, borderColor: '#e5e7eb' },
  panelHeader: { alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b' },
  inputGroup: { marginBottom: 16 },
  inputRow: { flexDirection: 'row', gap: 16 },
  half: { flex: 1 },
  label: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  helperText: { fontSize: 12, color: '#64748b', marginTop: 6 },
  inputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb', paddingHorizontal: 12, height: 48 },
  currencySymbol: { fontSize: 16, color: '#64748b', marginRight: 8 },
  percentSymbol: { fontSize: 16, color: '#64748b', marginLeft: 8 },
  suffixText: { fontSize: 14, color: '#64748b', marginLeft: 8 },
  input: { flex: 1, fontSize: 16, color: '#0f172a', borderWidth: 0 },
  resultsContainer: { gap: 12 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14, color: '#334155' },
  summaryValue: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#065f46' },
  placeholderText: { fontSize: 14, color: '#64748b' },
  chartSection: { marginTop: 12, alignItems: 'center' },
  chartTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  infoSection: { marginVertical: 20, marginHorizontal:20, backgroundColor: '#ffffff', padding: 20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 12 },
  sectionText: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 12 },
  faqContainer: { marginBottom: 20, marginHorizontal:20 },
  faqHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, backgroundColor: '#4F46E510', borderRadius: 12 },
  faqTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' },
  faqContent: { backgroundColor: '#ffffff', padding: 20, borderRadius: 12, marginTop: 12, borderWidth: 1, borderColor: '#e5e7eb' },
  faqItem: { marginBottom: 16 },
  faqQuestion: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 6 },
  faqAnswer: { fontSize: 15, color: '#475569', lineHeight: 22 },
  seoContainer: { backgroundColor: '#ffffff', padding: 20, marginHorizontal:20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, marginBottom: 20 },
  seoTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a', marginBottom: 12, textAlign: 'center' },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  linkButton: { paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#4F46E510', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb' },
  linkText: { color: '#4F46E5', fontWeight: '600' },
  disclaimer: { fontSize: 12, color: '#64748b', marginTop: 4 },
  seoSubtitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 8, marginBottom: 6 },
});

export default FlatVsReducingRateCalculatorScreen;